/**
 * View attention (BE-2151) — the surfaced-to-shown report for one visit, answering
 * "was the right thing put in front of the right person in time". Per surfaced
 * view: when the agent put it up, who it was aimed at, and who the system served
 * it to. Reached at `/attention?visit=<uuid>`, leadership only.
 *
 * Two claims this deliberately does NOT make, both of which it made earlier and
 * both of which accuse people wrongly:
 *
 * - It never says "seen". The backend records that a payload was served, not that
 *   a human read it, so every phrase here is "shown"/"served". Where the clinic
 *   needs a human to attest they read something, that is signing — a deliberate
 *   act with an audit trail, not telemetry.
 * - It never counts a row nobody was served as a staff failure without checking
 *   why. `leadershipOnly` and `audiences` separate "RLS hid it from them" and
 *   "aimed at a role nobody in it was served" from a genuine miss.
 */
import type { components } from "@api/schema";
import { useSessionStore } from "@auth/sessionStore";
import { AsyncBoundary } from "@components/AsyncBoundary";
import { CompanionHeader } from "@components/CompanionHeader";
import { useUser } from "@context/UserContext";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCheck,
  faEyeSlash,
  faStopwatch,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  cn,
  PanelError,
  Skeleton,
  StatusMark,
  Swipe,
  type SwipeTone,
  Tag,
} from "@scribenote/brian-design-system";
import { Navigate, useSearch } from "@tanstack/react-router";
import { canViewAttention, type ClinicUser } from "@users/userClient";
import { useClinicUsers } from "@users/userQueries";
import { formatDateTime } from "@utils/format-date";
import { useNow } from "@utils/use-now";
import {
  formatDuration,
  formatFirstSeen,
  formatViewerLatency,
} from "@views/attentionFormat";
import {
  attentionOutcome,
  isStaffFinding,
  isTargeted,
  rosterDecides,
  useVisitAttention,
  type AttentionOutcome,
  type AttentionRow,
  type AttentionScope,
} from "@views/attentionQueries";
import type { Audience } from "@views/viewsClient";

/** Short labels for the role scopes a row names; a null scope is simply omitted. */
const ROLE_LABELS: Record<components["schemas"]["SystemRole"], string> = {
  LEADERSHIP: "Leadership",
  CLINICAL_DVM: "DVM",
  CLINICAL_STAFF: "Staff",
  OPS: "Ops",
};

/** A stage carries its own name; the visit and patient scopes are named here so
 * a stage that happens to be called "Patient" stays distinguishable. */
const scopeLabel = (scope: AttentionScope): string =>
  scope.kind === "stage" ? (scope.name ?? "a stage") : `the ${scope.kind}`;

/** "DVM and everyone" rather than a comma list. `Intl.ListFormat` would do this,
 * but the shared tsconfig targets ES2020 and it lands in ES2021. */
const audienceList = (labels: string[]): string =>
  labels.length > 1
    ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
    : labels.join("");

/**
 * Who one audience covers. Both identity fields null is the backend's
 * "everyone" (`audience_matches` returns true for it), which is exactly who a
 * viewer outside the targeted role matched — so it is named, not blanked.
 */
const audienceLabel = (audience: Audience, users: ClinicUser[]): string => {
  if (audience.systemRole)
    return ROLE_LABELS[audience.systemRole] ?? audience.systemRole;
  if (audience.userId === null) return "everyone";
  return (
    users.find((u) => u.id === audience.userId)?.displayName ?? "one person"
  );
};

/**
 * The reading measure the report is written to: the masthead and every entry
 * share it, so a wide monitor doesn't stretch a line of small print across three
 * feet of paper while the ruling still spans the page.
 */
const MEASURE = "mx-auto w-full max-w-4xl";

const AttentionSkeleton = () => (
  <div className={cn("flex flex-col gap-4 p-6", MEASURE)}>
    <Skeleton index={0} className="h-8 w-64" />
    <Skeleton index={1} className="h-20" />
    <Skeleton index={2} className="h-20" />
  </div>
);

export const AttentionShell = () => {
  const { visit } = useSearch({ from: "/attention" });
  const { currentUser } = useUser();

  if (!currentUser) return null;
  if (!canViewAttention(currentUser)) return <Navigate to="/" />;

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-paper text-ink-body">
      {visit ? (
        <AsyncBoundary
          skeleton={<AttentionSkeleton />}
          fallback={(retry) => (
            <PanelError label="view attention" onRetry={retry} />
          )}
          resetKeys={[visit]}
        >
          <AttentionInner visitUuid={visit} />
        </AsyncBoundary>
      ) : (
        <>
          <CompanionHeader
            back={{ title: "View attention", testId: "attention-back-empty" }}
          />
          <div className="flex flex-1 items-center justify-center">
            <span className="text-sm text-annotation">
              No visit selected — open a visit&rsquo;s attention from its card,
              or pass ?visit=&lt;uuid&gt;.
            </span>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * The whole report said in one marked word: the green marker when every surfacing
 * reached someone, the flag when any of them didn't. A visit with nothing surfaced
 * gets no mark at all — there is no verdict to reach, and the line under it says
 * exactly that.
 *
 * Never `brian`: the blue marker means Brian did something, and delivery is the
 * system's doing, not his.
 */
const reportVerdict = (
  rowCount: number,
  findings: number,
): { label: string; tone: SwipeTone } | null => {
  if (rowCount === 0) return null;
  return findings > 0
    ? { label: "Not all shown", tone: "flag" }
    : { label: "All shown", tone: "working" };
};

const AttentionInner = ({ visitUuid }: { visitUuid: string }) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { rows, outsideVisit, unboundedHistory, visit } =
    useVisitAttention(visitUuid);
  // Non-suspense on purpose: names are an optional lookup that falls back to
  // "Someone", so a failed roster fetch must not replace the whole report with an
  // error. `useUsersList` would, being suspense-flavoured.
  const { data: users, isError: rosterFailed } = useClinicUsers(sessionKey);
  // Doubles as the classifier's roster: who holds which roles decides whether a
  // leadership-only row's audience was really unreachable (BE-2279). Absent it,
  // nothing is excused, which is the safe direction.
  const roster = users ?? [];
  // A roster that never arrived is not a clinic where nobody holds the role: it
  // excuses nothing, so rows that would read `notEligible` land in the headline as
  // findings instead. Disclosed rather than left to read as settled, since accusing
  // people on the strength of a failed fetch is the direction this file forbids.
  // Keyed off the failure, not the absence: a loaded-but-empty roster is a real
  // answer, and a roster still in flight becomes one in a moment, so warning while
  // it is merely pending just flashes at anyone opening this cold.
  const rosterDegraded = rosterFailed && rows.some(rosterDecides);

  // Only rows that assert staff weren't shown something count. A row hidden from
  // non-leadership staff by RLS is a visibility fact, not their failure, so it is
  // disclosed separately rather than folded into the headline.
  const findings = rows.filter((row) => isStaffFinding(row, roster)).length;
  const notEligible = rows.filter(
    (row) => attentionOutcome(row, roster) === "notEligible",
  ).length;
  const verdict = reportVerdict(rows.length, findings);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <CompanionHeader
        back={{
          title: visit.reason ?? visit.visitType,
          testId: "attention-back",
        }}
      />

      {/* One band, not two: the disclosures sit with the summary they qualify.
          "no record of being shown" is a strong claim, so what counts as shown —
          and any patient history held out — are stated on screen rather than
          tucked into a tooltip.

          The masthead of the report: a mono eyebrow naming what the sheet is,
          the verdict in one marked word, the count in plain ink under it, and the
          fine print in a well cut into the page. */}
      <div className="border-b border-rule px-4 py-3">
        <div className={cn("flex flex-col gap-1.5", MEASURE)}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="min-w-0 truncate text-annotation-caps text-annotation">
              Surfaced to shown
            </span>
            {verdict && (
              <span className="shrink-0 text-xs">
                <Swipe tone={verdict.tone} data-testid="attention-verdict">
                  {verdict.label}
                </Swipe>
              </span>
            )}
          </div>
          {/* The verdict is already marked above, so the count is written in
              plain ink: one highlighter per state, not two. */}
          <p
            className="font-display text-sm font-medium text-ink"
            data-testid="attention-summary"
          >
            {rows.length === 0
              ? "Nothing surfaced on this visit, its patient, or its stages."
              : findings > 0
                ? `${findings} of ${rows.length} surfacings with no record of being shown`
                : `All ${rows.length} surfacings were shown to someone`}
          </p>
          {/* The fine print, sunk into the page: what the counts mean, and every
              way they fall short of the whole truth. Only the two lines that say
              the verdict itself may be wrong carry the flag ink; the rest state
              scope, which is not a warning. */}
          <div className="flex flex-col gap-1 rounded-paper bg-recess px-3 py-2">
            <p className="text-xs text-annotation">
              Who the system served each view to — delivery, not proof anyone
              read it. A signature is the only record of a human attesting to
              that.
            </p>
            {/* The backend records a sighting only when a view was fetched on its
                own or served in a subject's slot (BE-2151), so what does *not*
                count is stated rather than left for the reader to assume. */}
            <p className="text-xs text-annotation" id="attention-signal-note">
              Counting only views fetched on their own or served in a
              subject&rsquo;s slot. A view someone passed on a list page reads
              as never shown.
            </p>
            {notEligible > 0 && (
              <p
                className="text-xs text-annotation"
                data-testid="attention-not-eligible"
              >
                {notEligible} {notEligible === 1 ? "surfacing" : "surfacings"}{" "}
                {notEligible === 1 ? "was" : "were"} leadership-only but aimed
                only at roles it hides from, so nobody eligible could be served
                — a routing fault, not a staff finding.
              </p>
            )}
            {rosterDegraded && (
              <p
                className="text-xs text-ink-flag"
                data-testid="attention-roster-unavailable"
              >
                The clinic roster couldn&rsquo;t be loaded, so leadership-only
                surfacings couldn&rsquo;t be excused: some counted above may be
                routing faults rather than staff findings.
              </p>
            )}
            {unboundedHistory && (
              <p
                className="text-xs text-ink-flag"
                data-testid="attention-unbounded-history"
              >
                This visit has no scheduled start and hasn&rsquo;t entered a
                stage, so the patient&rsquo;s surfacings couldn&rsquo;t be
                narrowed to it — earlier visits may be counted below.
              </p>
            )}
            {outsideVisit > 0 && (
              <p
                className="text-xs text-annotation"
                data-testid="attention-outside-visit"
              >
                {outsideVisit} {outsideVisit === 1 ? "surfacing" : "surfacings"}{" "}
                on this patient fall outside this visit and aren&rsquo;t
                counted.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Rules live on the rows, not a `divide-y` on this container: Tailwind
          v4's `divide-y` sizes `:not(:last-child)` while a v3-era colour
          utility still paints `& > * + *`, so the first row's rule would miss
          the colour and fall back to currentColor. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {rows.map((row) => (
          <AttentionRowItem
            key={`${row.scope.entityId}-${row.viewId}`}
            row={row}
            users={roster}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * The row's outcome said in a word, wearing the highlighter that means it: the
 * green marker for the view that landed, the flag for the one nobody has been
 * served. The word is the point — a coloured tick on its own makes the reader
 * guess which of the three states they are looking at.
 *
 * `notEligible` goes unmarked on purpose. RLS hiding a view from every audience
 * it named is a fact about visibility, not a state anyone is in, and a
 * highlighter there would read as the accusation this report is careful never to
 * make. Plain annotation ink, the same treatment a cancelled visit gets.
 *
 * `brian` never appears: delivery is the system's doing, not his.
 */
const OUTCOME_MARK: Record<
  AttentionOutcome,
  { label: string; icon: IconDefinition; tone: SwipeTone | null }
> = {
  delivered: { label: "Shown", icon: faCheck, tone: "working" },
  audienceMissed: { label: "Not shown", icon: faStopwatch, tone: "flag" },
  nobodyServed: { label: "Not shown", icon: faStopwatch, tone: "flag" },
  notEligible: { label: "Nobody eligible", icon: faEyeSlash, tone: null },
};

/** The outcome as icon + word, under its highlighter where it has one. */
const OutcomeMark = ({
  outcome,
  testId,
}: {
  outcome: AttentionOutcome;
  testId: string;
}) => {
  const mark = OUTCOME_MARK[outcome];
  const icon = <FontAwesomeIcon icon={mark.icon} />;

  return mark.tone ? (
    <Swipe
      tone={mark.tone}
      icon={icon}
      className="shrink-0 text-annotation-caps"
      data-testid={`attention-outcome-${testId}`}
    >
      {mark.label}
    </Swipe>
  ) : (
    <StatusMark
      icon={icon}
      className="shrink-0"
      data-testid={`attention-outcome-${testId}`}
    >
      {mark.label}
    </StatusMark>
  );
};

/**
 * One surfaced view, written as one entry on the report: the view's name, the
 * facts it carries, its outcome in a marked word, and the delivery lines under
 * it. A row nobody was shown is the report's finding rather than an empty state —
 * but only when staff could have seen it, so the outcome decides the treatment: a
 * flag for a real finding, unmarked for a leadership-only row that
 * non-leadership staff were never eligible to receive.
 *
 * Entries are ruled lines on the page rather than lifted cards: this is a
 * delivery record read top to bottom, and a deck of twenty pieces of paper would
 * space out and lift what is meant to scan as one continuous sheet.
 *
 * Findings carry how long they have gone unshown: a view surfaced a minute ago is
 * not the same finding as one nobody has been served in three days.
 *
 * `users` is the clinic roster, which both resolves names and feeds the outcome.
 */
const AttentionRowItem = ({
  row,
  users,
}: {
  row: AttentionRow;
  users: ClinicUser[];
}) => {
  const now = useNow();
  const outcome = attentionOutcome(row, users);
  const finding = isStaffFinding(row, users);
  const testId = `${row.scope.entityId}-${row.viewId}`;
  // Targeted first, so the specific audience leads and "everyone" reads as the
  // widening it is.
  const audiences = [
    ...row.audiences.filter(isTargeted),
    ...row.audiences.filter((a) => !isTargeted(a)),
  ];

  return (
    // The rule runs the width of the page while the entry keeps to the reading
    // measure, the way a ruled sheet is written on.
    <div className="border-b border-rule last:border-b-0">
      <div className={cn("flex min-w-0 flex-col px-4 py-3", MEASURE)}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p
            className="min-w-0 flex-1 truncate font-display text-sm font-medium text-ink"
            title={row.title}
          >
            {row.title}
          </p>
          {/* Facts, not states: which scope the view was surfaced on and whether
              RLS narrows who can see it. The highlighter is reserved for the
              row's outcome. */}
          {row.leadershipOnly && (
            <Tag
              className="shrink-0"
              data-testid={`attention-leadership-only-${testId}`}
            >
              Leadership only
            </Tag>
          )}
          <Tag className="shrink-0" data-testid={`attention-scope-${testId}`}>
            on {scopeLabel(row.scope)}
          </Tag>
          <OutcomeMark outcome={outcome} testId={testId} />
        </div>
        <p
          className={cn(
            "text-xs",
            // The outcome is marked above, so this line only tints where it
            // carries the finding's own weight: how long it has gone unshown.
            finding ? "text-ink-flag" : "text-annotation",
          )}
          data-testid={`attention-latency-${testId}`}
        >
          Surfaced {formatDateTime(row.surfacedAt)}
          {/* Clamped: the browser clock can sit behind the server's, and a
              just-surfaced view must not read as surfaced in the future. */}
          {finding &&
            ` (${formatDuration(Math.max(0, now - Date.parse(row.surfacedAt)) / 1000)} ago)`}{" "}
          · {formatFirstSeen(row.secondsToFirstSeen)}
        </p>
        {/* Naming who it was aimed at is what turns "nobody saw it" into a
            routing finding. A generic audience is listed alongside a targeted one
            rather than hidden: it is what a viewer outside the targeted role
            matched, so hiding it makes their latency — or their "opened it
            directly" — look like a contradiction. A row aimed at nobody in
            particular still says nothing worth a line. */}
        {audiences.some(isTargeted) && (
          <p
            className="min-w-0 text-xs text-annotation"
            data-testid={`attention-audience-${testId}`}
          >
            Aimed at{" "}
            {audienceList(audiences.map((a) => audienceLabel(a, users)))}
          </p>
        )}
        {row.viewers.length > 0 && (
          <p className="mt-1 flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-xs text-annotation">
            {row.viewers.map((viewer) => {
              const name =
                users.find((u) => u.id === viewer.userId)?.displayName ??
                "Someone";
              // The stamp is the audience scope that reached them, not their own
              // role (BE-2279), so it reads as "via" — null on a direct read or a
              // generic match, which is the common case.
              const via = viewer.systemRole
                ? ROLE_LABELS[viewer.systemRole]
                : null;
              return (
                // A display name has no guaranteed break point, so it carries
                // its own min-w-0 rather than relying on the wrapping row.
                <span key={viewer.userId} className="min-w-0 break-words">
                  {name}
                  {via ? ` (via ${via})` : ""}{" "}
                  <span className="text-ink">
                    {formatViewerLatency(viewer.secondsToFirstSeen)}
                  </span>
                </span>
              );
            })}
          </p>
        )}
      </div>
    </div>
  );
};
